import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { UserName } from "@/components/UserName";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Boxes, ChevronRight, History, Loader2, MapPin, Search, Truck, User } from "lucide-react";
import { SearchCameraButton } from "@/components/search/engine";

const PAGE = 25;

// ONE LOT PICKER, TWO DENSITIES — his instruction 2026-10-07: "Use the SAME lot picker as the desk
// screen, not a second version — one implementation, sized for gloves on the floor."
//
// What it fixes. The desk list loaded every open lot in one bare array capped at 500 and filtered
// what had loaded; the FLOOR list loaded every open lot with no search box at all, which in
// production is 920 buttons. PRODUCTION MEASURED: 420 open lots (399 legacy) were never in the
// list and could not be found by typing, and the screen's one call took a median of 7,758 ms.
// Now: the page, the search and the counts all come from the server, every lot is reachable, and
// the lots the crew has actually been working sit on top.

const ago = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.round(ms / 60000);
  if (m < 60) return m <= 1 ? "just now" : `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d} days ago`;
};

const LotRow = ({ lot: l, floor, onPick, testid, worked }) => (
  <div data-testid={testid} role="button" tabIndex={0}
    onClick={() => onPick(l)}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(l); } }}
    className={`w-full cursor-pointer text-left bg-white flex items-center gap-3 transition-colors ${floor
      ? "rounded-xl border-2 border-[#E2E8F0] p-4 min-h-[84px] active:scale-[0.99] active:bg-slate-50"
      : "rounded-xl border border-slate-200 p-4 hover:border-[#E0781A]"}`}>
    <Boxes className={`${floor ? "w-7 h-7" : "w-5 h-5"} text-slate-400 shrink-0`} />
    <div className="min-w-0 flex-1">
      <div className={`font-mono font-bold text-slate-800 flex items-center gap-2 ${floor ? "text-lg" : ""}`}>
        {l.batch_number}
        {l.kind === "legacy"
          ? <Badge className="rounded-full border-0 bg-[#F6F1FB] text-[#5E3A93] text-[9px] font-mono px-1.5">LEGACY</Badge>
          : <Badge className="rounded-full border-0 bg-[#FFF1E8] text-[#C76714] text-[9px] font-mono px-1.5">DONOR</Badge>}
        {l.status === "closed" && (
          <Badge data-testid={`lot-closed-${l.batch_number}`} className="rounded-full border-0 bg-slate-800 text-white text-[9px] font-mono px-1.5">CLOSED</Badge>
        )}
      </div>
      <div className={`text-slate-500 truncate ${floor ? "text-base" : "text-sm"}`}>{l.label}</div>
      {l.donor && (
        <div className={`text-[#C76714] flex items-center gap-1 mt-0.5 flex-wrap ${floor ? "text-sm" : "text-[11px]"}`}>
          <Truck className="w-3.5 h-3.5 shrink-0" />
          <span className="font-mono font-semibold">{l.donor.stock_number}</span>
          {l.donor.ymm && <span className="text-slate-500">· {l.donor.ymm}</span>}
          {!floor && l.donor.vin && <span className="font-mono text-slate-400">· VIN {l.donor.vin}</span>}
        </div>
      )}
      {!floor && l.created_by && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
          <User className="w-3 h-3" /><UserName email={l.created_by} />
        </div>
      )}
      {l.current_location?.placed && (
        <div className={`text-slate-500 flex items-center gap-1 mt-0.5 ${floor ? "text-sm" : "text-[11px]"}`}>
          <MapPin className="w-3.5 h-3.5 text-[#C76714]" />{l.current_location.label}
        </div>
      )}
      {worked && (
        <div data-testid={`lot-worked-${l.batch_number}`}
          className={`text-emerald-700 font-semibold flex items-center gap-1 mt-0.5 ${floor ? "text-sm" : "text-[11px]"}`}>
          <History className="w-3.5 h-3.5" />Last tagged {ago(worked)}
        </div>
      )}
    </div>
    <div className="shrink-0 flex items-center gap-2">
      <div className="text-right">
        <div className={`font-mono font-bold text-slate-800 ${floor ? "text-2xl" : "text-lg"}`}>{l.total_count ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">parts</div>
        {/* STILL IN THE BIN — the number that says whether this lot is finished (his rule,
            2026-10-14). A lot cannot close until it reads none. */}
        {l.in_crate > 0 && (
          <div data-testid={`lot-in-crate-${l.batch_number}`}
            className={`mt-0.5 font-mono font-bold text-[#C76714] ${floor ? "text-base" : "text-[11px]"}`}>
            {l.in_crate} in bin
          </div>
        )}
        {l.in_crate === 0 && (l.total_count ?? 0) > 0 && (
          <div data-testid={`lot-bin-empty-${l.batch_number}`}
            className={`mt-0.5 font-semibold text-emerald-700 ${floor ? "text-base" : "text-[11px]"}`}>
            bin empty
          </div>
        )}
      </div>
      {floor && <ChevronRight className="w-6 h-6 text-slate-300" />}
    </div>
  </div>
);

export const LotPicker = ({ variant = "desk", onPick, reloadKey = 0 }) => {
  const floor = variant === "floor";
  const [lots, setLots] = useState(null);     // null = loading; [] = loaded, nothing matches
  const [meta, setMeta] = useState(null);     // {total, shown, has_more, page, pages, counts}
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("open");   // open | closed — closing is not hiding
  const [nearlyDone, setNearlyDone] = useState(false);   // only a few parts left in the bin
  const [recent, setRecent] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (page = 1) => {
    if (page === 1) setLots(null);
    try {
      const { data } = await api.get("/capture-batches", {
        params: { paged: true, page, page_size: PAGE, q: query.trim(),
                  kind: kind === "all" ? "" : kind, status,
                  ...(nearlyDone ? { nearly_done: true } : {}) },
      });
      setLots((prev) => (page === 1 ? (data.items || []) : [...(prev || []), ...(data.items || [])]));
      setMeta(data);
    } catch { setLots([]); setMeta(null); }
  }, [query, kind, status, nearlyDone]);

  // Typing waits a beat, so a search is one request and not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { load(1); }, query.trim() ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, reloadKey]);

  // The lots the crew has actually tagged into, newest first. Derived on the server from the parts
  // themselves, so it is true for lots that existed long before this screen did.
  useEffect(() => {
    let live = true;
    api.get("/capture-batches/recent", { params: { limit: floor ? 4 : 5 } })
      .then(({ data }) => { if (live) setRecent(data.items || []); })
      .catch(() => { if (live) setRecent([]); });
    return () => { live = false; };
  }, [floor, reloadKey]);

  // Typing a query OR tapping "nearly finished" is a deliberate hunt — the "where you left off"
  // shortcuts step aside, because then the filter is the intent.
  const searching = !!query.trim() || nearlyDone;
  const inputH = floor ? "h-14 text-base" : "h-10";
  // A lot that is already in the band above is not drawn twice below it.
  const recentIds = new Set(searching ? [] : recent.map((r) => r.id));
  const visible = (lots || []).filter((l) => !recentIds.has(l.id));

  return (
    <div data-testid="lot-picker">
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search className={`${floor ? "w-5 h-5 left-4" : "w-4 h-4 left-3"} text-slate-400 absolute top-1/2 -translate-y-1/2`} />
          <Input data-testid="lots-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={floor ? "Search every lot — number, name, crate, truck…"
              : "Search every lot — number, name, creator, crate code, or its truck…"}
            className={`${floor ? "pl-12" : "pl-9"} ${inputH} rounded-lg border-slate-200`} />
        </div>
        <SearchCameraButton testid="lots-search" onScan={setQuery} size={floor ? "h-14" : "h-10"} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-2 mb-2">
        {[["all", "All"], ["legacy", "Legacy"], ["standard", "Donor"]].map(([k, lbl]) => (
          <button key={k} type="button" data-testid={`lots-kind-${k}`} onClick={() => setKind(k)}
            className={`rounded-full border font-semibold transition-colors ${floor ? "px-4 h-11 text-sm" : "px-3 h-7 text-xs"} ${kind === k
              ? "border-[#E0781A] bg-[#FFF9F3] text-[#C76714]"
              : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
            {lbl}{meta?.counts && k !== "all" ? ` (${k === "legacy" ? meta.counts.legacy : meta.counts.standard})` : ""}
          </button>
        ))}
        {/* CLOSING A LOT IS "DONE MOVING", NOT "GONE" — a closed lot stays listed and searchable. */}
        <button type="button" data-testid="lots-status-closed"
          onClick={() => setStatus(status === "closed" ? "open" : "closed")}
          className={`rounded-full border font-semibold transition-colors ${floor ? "px-4 h-11 text-sm" : "px-3 h-7 text-xs"} ${status === "closed"
            ? "border-slate-700 bg-slate-800 text-white"
            : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
          Closed{meta?.counts ? ` (${meta.counts.closed ?? 0})` : ""}
        </button>
        {/* NEARLY FINISHED — a few parts left in the bin, biggest lot first. One tap, and the
            number of lots is on the chip so he knows before he taps (his instruction, 2026-10-14). */}
        <button type="button" data-testid="lots-nearly-done"
          onClick={() => setNearlyDone((v) => !v)}
          className={`rounded-full border font-semibold transition-colors ${floor ? "px-4 h-11 text-sm" : "px-3 h-7 text-xs"} ${nearlyDone
            ? "border-[#C76714] bg-[#FFF1E8] text-[#C76714]"
            : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
          Nearly finished{meta?.counts?.nearly_done !== undefined ? ` (${meta.counts.nearly_done})` : ""}
        </button>
        {meta && (
          <span data-testid="lots-showing" className={`ml-auto font-mono text-slate-500 ${floor ? "text-xs" : "text-[11px]"}`}>
            {meta.total === 0
              ? "no lots match"
              : meta.shown >= meta.total
                ? `showing all ${meta.total}`
                : `showing ${meta.shown} of ${meta.total}`}
            {nearlyDone ? ` with ${meta.nearly_done_max ?? 5} or fewer left in the bin`
              : searching ? "" : status === "closed" ? " closed" : ` open · ${meta.counts?.closed ?? 0} closed`}
          </span>
        )}
      </div>

      {/* THE FEW LOTS THEY ACTUALLY WORK, without searching for them. Hidden while searching,
          because then the typed query is the intent. */}
      {!searching && recent.length > 0 && (
        <div data-testid="lots-recent" className="mb-3">
          <div className={`font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5 mb-1.5 ${floor ? "text-xs" : "text-[11px]"}`}>
            <History className={floor ? "w-4 h-4" : "w-3.5 h-3.5"} />Picked up where you left off
          </div>
          <div className="space-y-2">
            {recent.map((l) => (
              <LotRow key={`recent-${l.id}`} lot={l} floor={floor} onPick={onPick}
                testid={`lot-recent-${l.batch_number}`} worked={l.last_worked_at} />
            ))}
          </div>
        </div>
      )}

      {!searching && recent.length > 0 && (
        <div className={`font-bold uppercase tracking-wider text-slate-500 mb-1.5 ${floor ? "text-xs" : "text-[11px]"}`}>
          All open lots
        </div>
      )}
      <div className="space-y-2" data-testid="lots-list">
        {lots === null && (
          <div data-testid="lots-loading" className="flex items-center justify-center gap-2 py-10 text-slate-400">
            <Loader2 className={floor ? "w-8 h-8 animate-spin" : "w-6 h-6 animate-spin"} />
            <span className="text-sm font-medium">Loading lots…</span>
          </div>
        )}
        {lots !== null && lots.length === 0 && searching && !nearlyDone && (
          <div data-testid="lots-empty-search" className="border border-dashed border-slate-200 rounded-xl p-8 text-center text-sm text-slate-400">
            No lots match “{query}”.
          </div>
        )}
        {lots !== null && nearlyDone && lots.length === 0 && (
          <div data-testid="lots-nearly-done-empty" className="border border-dashed border-slate-200 rounded-xl p-8 text-center text-sm text-slate-400">
            No lot has a few parts left in its bin right now — nothing is nearly finished.
          </div>
        )}
        {lots !== null && lots.length === 0 && !searching && !nearlyDone && (
          <div data-testid="lots-empty" className={`border-2 border-dashed border-slate-200 rounded-xl p-6 text-center text-slate-500 ${floor ? "text-lg font-bold" : "text-sm"}`}>
            {floor ? "No open lots. Start a new lot at a desk, then scan into it here."
              : "No open lots yet. Create one above to start capturing."}
          </div>
        )}
        {(visible || []).map((l) => (
          <LotRow key={l.id} lot={l} floor={floor} onPick={onPick} testid={`lot-${l.batch_number}`} />
        ))}
      </div>

      {/* THE REST OF THEM ARE REACHABLE. There is no page past which a lot stops existing. */}
      {meta?.has_more && (
        <Button data-testid="lots-load-more" variant="outline" disabled={loadingMore}
          onClick={async () => {
            setLoadingMore(true);
            try { await load((meta.page || 1) + 1); } finally { setLoadingMore(false); }
          }}
          className={`mt-3 w-full rounded-lg border-slate-200 text-slate-600 hover:border-[#E0781A] hover:text-[#C76714] ${floor ? "h-14 text-base font-bold" : "h-11"}`}>
          {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
          Show more — {meta.total - meta.shown} still to come
        </Button>
      )}
    </div>
  );
};
